import { useDndContext, useDraggable, useDroppable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { Button, Tooltip, useTheme } from '@mui/material';
import {
  Workout,
  WorkoutPublishErrors,
  WorkoutSection,
} from '@strength/domain-workout';
import { nextSortOrder } from '@strength/utils';
import { MdDragIndicator, MdOutlineNote } from '@teamworksdev/icons';
import { useAtom } from 'jotai';
import { ReactNode, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CELL_CLASSNAME_SELECTED } from '../../../components/sheet';
import { CellInputWithAtom } from '../../../components/sheet/slots/common';
import { CustomSlot } from '../../../components/sheet/slots/custom-slot';
import {
  ADD_SECTION_SLOT,
  SECTION_NAME_SLOT,
} from '../../../components/sheet/slots/slot-labels';
import { cn } from '../../../components/utils';
import { useViewer } from '../../../context/viewer';
import { toast } from '../../../hooks/useToast';
import i18n from '../../../utils/i18n';
import { jotaiStore } from '../../../utils/jotai';
import { WorkoutTemplateData } from '../../library/workout-templates/workout-template-utils';
import { AdvancedWorkoutTemplateSearchModal } from '../advanced-search/AdvancedWorkoutTemplateSearchModal';
import {
  DROPPABLE_CLASSNAME,
  WorkoutSectionDraggable,
  WorkoutSectionDroppable,
  sectionDndStyleInfo,
} from '../dnd';
import { WorkoutLayerMutationFns } from '../mutation-fns/types';
import { BlockSlot } from '../types';
import { CollapseSlotIcon } from './CollapseSlotIcon';
import {
  SlotsResults,
  blockSectionAndExerciseLabelStyle,
  collapsedSectionsAtom,
  inputAtom,
} from './common';

export function getSectionSlots(
  workout: Workout,
  section: WorkoutSection,
  sectionKey: string,
  errors: WorkoutPublishErrors | null,
  x: number,
  y: number,
  workoutWidth: number,
  isReadOnly = false,
  mutationFns: WorkoutLayerMutationFns,
  gridOptions?: {
    style?: React.CSSProperties;
    overrideGridStyles?: boolean;
  },
): SlotsResults {
  const slot: BlockSlot = new CustomSlot(
    { x, y },
    workoutWidth,
    1,
    isReadOnly,
    (editing, onBlur) => (
      <Section
        editing={editing}
        onBlur={onBlur}
        section={section}
        errors={errors}
        isReadOnly={isReadOnly}
        sectionKey={sectionKey}
      />
    ),
    {
      data: [
        {
          type: 'Section',
          workoutId: section.workoutId,
          section,
          disabled: isReadOnly,
        },
      ],
      editBehavior: {
        type: 'input',
        onStart: (initInput) => {
          jotaiStore.set(inputAtom, initInput ?? section.name);
        },
        onCommit: () => {
          const input = jotaiStore.get(inputAtom);
          if (workout.sections.map((s) => s.name).includes(input)) {
            toast({
              title: i18n.t('sectionNameMustBeUnique'),
              severity: 'error',
            });
            return;
          }
          if (input.length > 0) {
            mutationFns.onEditSection(
              section.workoutId,
              section.workoutSectionId,
              {
                name: input,
              },
            );
          }
        },
      },
      style: gridOptions?.style,
      overrideGridStyles: gridOptions?.overrideGridStyles,
    },
    SECTION_NAME_SLOT,
  );

  return { slots: [slot], rowCount: 1 };
}

export const Section = ({
  editing,
  onBlur,
  section,
  errors,
  isReadOnly,
  sectionKey,
}: {
  editing: boolean;
  onBlur: () => void;
  section: WorkoutSection;
  errors: WorkoutPublishErrors | null;
  isReadOnly: boolean;
  sectionKey: string;
}) => {
  if (editing && !isReadOnly) {
    return <CellInputWithAtom inputAtom={inputAtom} onBlur={onBlur} />;
  }
  return (
    <SectionLabelDraggable
      section={section}
      errors={errors}
      isReadOnly={isReadOnly}
      sectionKey={sectionKey}
    />
  );
};

const SectionLabelDraggable = ({
  section,
  errors,
  isReadOnly,
  sectionKey,
}: {
  section: WorkoutSection;
  errors: WorkoutPublishErrors | null;
  isReadOnly: boolean;
  sectionKey: string;
}) => {
  const {
    palette: { greyscaleLight },
  } = useTheme();
  const id = sectionKey;
  const data: WorkoutSectionDraggable = {
    workoutSectionId: section.workoutSectionId,
    type: 'WorkoutSection',
    workoutId: section.workoutId,
  };

  const {
    attributes,
    listeners,
    setNodeRef: setDraggableRef,
    setActivatorNodeRef,
    transform,
    active,
  } = useDraggable({
    id,
    data,
    disabled: isReadOnly,
  });

  // SC-5486: pointer drag from the whole label; keyboard drag stays on the
  // drag icon. The label is input-editable, and dnd-kit's KeyboardSensor
  // activates on Enter/Space — the same keys the sheet uses to start
  // editing — so the keyboard listener must not sit on the label itself.
  const { onKeyDown: keyboardDragListener, ...pointerDragListeners } =
    listeners ?? {};
  const { isOver, setNodeRef: setDroppableRef } = useDroppable({
    id,
    data,
  });

  const [hovering, setHovering] = useState(false);

  const { showDragIcon, highlight, isDragging } = sectionDndStyleInfo(
    active,
    hovering,
    isOver,
    data,
  );

  const { t } = useTranslation();

  const sectionErrors = errors
    ?.get(section.workoutSectionId)
    ?.map((err) => t(...err));

  const content = (
    <div
      className={cn('relative h-full w-full', sectionErrors && 'bg-red-100')}
    >
      {/* The draggable part */}
      <div
        onMouseEnter={() => setHovering(true)}
        onMouseLeave={() => setHovering(false)}
        ref={setDraggableRef}
        className={blockSectionAndExerciseLabelStyle}
        {...pointerDragListeners}
      >
        {/* SC-5486: pointer drag from the whole label; keyboard drag from
            this handle. The label is input-editable, and dnd-kit's
            KeyboardSensor activates on Enter/Space — the same keys the
            sheet uses to start editing — so the keyboard listener and the
            drag attributes live on the handle, not the label. The handle
            stays in the DOM (hidden until hover or keyboard focus) so
            keyboard-only users can reach it by tabbing. */}
        {!isReadOnly && (
          <div
            className={cn(
              'mr-1',
              showDragIcon
                ? 'opacity-100'
                : 'opacity-0 focus-visible:opacity-100',
            )}
            onKeyDown={
              keyboardDragListener as
                | React.KeyboardEventHandler<HTMLDivElement>
                | undefined
            }
            ref={setActivatorNodeRef}
            {...attributes}
          >
            <MdDragIndicator size={18} color={greyscaleLight[400]} />
          </div>
        )}
        <SectionLabel
          section={section}
          sectionKey={sectionKey}
          iconColor={greyscaleLight[400]}
        />
      </div>

      {/* The draggable display */}
      {isDragging && (
        <div
          className={`${CELL_CLASSNAME_SELECTED} absolute top-0 left-0 z-30 flex h-full w-full items-center bg-white`}
          style={{ transform: CSS.Translate.toString(transform) }}
        >
          <MdDragIndicator
            className="mr-1"
            size={18}
            color={greyscaleLight[400]}
          />
          <SectionLabel
            section={section}
            sectionKey={sectionKey}
            iconColor={greyscaleLight[400]}
          />
        </div>
      )}

      {/* The droppable part */}
      <div
        className={cn(
          'absolute top-0 left-0 h-full w-full',
          highlight && DROPPABLE_CLASSNAME,
        )}
        ref={setDroppableRef}
      />
    </div>
  );

  if (sectionErrors) {
    return (
      <Tooltip title={sectionErrors.join(', ')} arrow>
        {content}
      </Tooltip>
    );
  }

  return content;
};

type AdvancedSearchState = { searchTerm: string; open: boolean };

const SectionLabel = ({
  section,
  sectionKey,
  iconColor,
}: {
  section: WorkoutSection;
  sectionKey: string;
  iconColor: string;
}) => {
  const { t } = useTranslation();
  const { instruction, color } = section;
  const {
    navbar: { navbarBottomColor },
  } = useViewer();
  const [showTooltip, setShowTooltip] = useState(false);
  const [collapsedSections, setCollapsedSections] = useAtom(
    collapsedSectionsAtom,
  );
  const isCollapsed = collapsedSections.has(sectionKey);

  const toggleCollapse = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setCollapsedSections((prev: Set<string>) => {
        const next = new Set(prev);
        if (next.has(sectionKey)) {
          next.delete(sectionKey);
        } else {
          next.add(sectionKey);
        }
        return next;
      });
    },
    [setCollapsedSections, sectionKey],
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleCollapse(e as unknown as React.MouseEvent);
    }
  };

  return (
    <Tooltip title={instruction} open={showTooltip}>
      <div className="overflow-hidden text-ellipsis whitespace-nowrap font-semibold text-base flex items-center justify-between w-full ml-1.5">
        <div className="flex items-center gap-1">
          <CollapseSlotIcon
            onClick={toggleCollapse}
            onKeyDown={handleKeyDown}
            isCollapsed={isCollapsed}
            name={`${t(isCollapsed ? 'expandSection' : 'collapseSection', {
              sectionName: section.name,
            })}`}
          />
          <div className="overflow-hidden text-ellipsis whitespace-nowrap min-w-0">
            {section.name}
          </div>
          {instruction && (
            <MdOutlineNote
              size={18}
              color={iconColor}
              onClick={(e) => {
                e.stopPropagation();
                setShowTooltip(true);
              }}
              onMouseLeave={() => setShowTooltip(false)}
            />
          )}
        </div>
        {color && color !== navbarBottomColor && (
          <div
            className="w-3 h-3 rounded-sm"
            style={{ backgroundColor: color }}
          />
        )}
      </div>
    </Tooltip>
  );
};

export function getAddSectionSlot(
  workout: Workout,
  workoutName: string,
  x: number,
  y: number,
  workoutWidth: number,
  mutationFns: WorkoutLayerMutationFns,
  onSelectWorkoutTemplate: (
    workout: Workout,
    workoutTemplate: WorkoutTemplateData,
  ) => void,
  setModalOrDrawerOpen: (open: boolean) => void,
  gridOptions?: {
    style?: React.CSSProperties;
    overrideGridStyles?: boolean;
  },
): SlotsResults {
  const slot: BlockSlot = new CustomSlot(
    { x, y },
    workoutWidth,
    1,
    false,
    (editing, onBlur, refocusSheet) => (
      <AddSection
        workoutId={workout.workoutId}
        editing={editing}
        onBlur={onBlur}
        workoutName={workoutName}
        setModalOrDrawerOpen={setModalOrDrawerOpen}
        onSelectWorkoutTemplate={(workoutTemplate) => {
          onSelectWorkoutTemplate(workout, workoutTemplate);
          // refocus the sheet on this slot position (x, y)
          refocusSheet(x, y);
          setModalOrDrawerOpen(false);
        }}
      />
    ),
    {
      data: [
        {
          type: 'AddSection',
          workoutId: workout.workoutId,
          workout,
        },
      ],
      editBehavior: {
        type: 'input',
        onStart: (input) => jotaiStore.set(inputAtom, input ?? ''),
        onCommit: () => {
          const input = jotaiStore.get(inputAtom);
          if (workout.sections.map((s) => s.name).includes(input)) {
            toast({
              title: i18n.t('sectionNameMustBeUnique'),
              severity: 'error',
            });
            return;
          }
          mutationFns.onAddSection(workout.workoutId, {
            name: input,
            sortOrder: nextSortOrder(workout.sections),
            instruction: null,
            color: null,
          });
        },
      },
      style: gridOptions?.style,
      overrideGridStyles: gridOptions?.overrideGridStyles,
    },
    ADD_SECTION_SLOT,
  );

  return {
    slots: [slot],
    rowCount: 1,
  };
}

const AddSection = ({
  editing,
  onBlur,
  workoutId,
  workoutName,
  setModalOrDrawerOpen,
  onSelectWorkoutTemplate,
}: {
  workoutId: string;
  editing: boolean;
  onBlur: () => void;
  workoutName: string;
  setModalOrDrawerOpen: (open: boolean) => void;
  onSelectWorkoutTemplate: (workoutTemplate: WorkoutTemplateData) => void;
}) => {
  const {
    palette: { greyscaleLight },
  } = useTheme();
  const { t } = useTranslation();
  const id = `AddWorkoutSection:${workoutId}`;
  const data: WorkoutSectionDroppable = {
    type: 'AddWorkoutSection',
    workoutId,
  };

  const { isOver, setNodeRef: setDroppableRef } = useDroppable({
    id,
    data,
  });

  const [hovering, setHovering] = useState(false);
  const { active } = useDndContext();

  const { showDragIcon, highlight } = sectionDndStyleInfo(
    active,
    false,
    isOver,
    data,
  );

  const [advancedWorkoutTemplateSearch, setAdvancedWorkoutTemplateSearch] =
    useState<AdvancedSearchState>({ searchTerm: '', open: false });

  let workoutTemplateBadge: ReactNode;
  if (hovering) {
    workoutTemplateBadge = (
      <NoWorkoutTemplateBadge
        setModalOrDrawerOpen={setModalOrDrawerOpen}
        setAdvancedWorkoutTemplateSearch={setAdvancedWorkoutTemplateSearch}
      />
    );
  }

  if (editing) {
    return <CellInputWithAtom inputAtom={inputAtom} onBlur={onBlur} />;
  }

  return (
    <span
      ref={setDroppableRef}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      className={cn(
        'flex text-base px-2.5 py-2 justify-between items-center w-full',
        highlight ? DROPPABLE_CLASSNAME : 'text-gray-500',
      )}
    >
      {showDragIcon && (
        <div className="mr-1">
          <MdDragIndicator size={18} color={greyscaleLight[400]} />
        </div>
      )}
      {t('addSection')}

      <div>{workoutTemplateBadge}</div>
      {advancedWorkoutTemplateSearch && (
        <AdvancedWorkoutTemplateSearchModal
          workoutName={workoutName}
          open={advancedWorkoutTemplateSearch.open}
          onClose={() => {
            setModalOrDrawerOpen?.(false);
            setAdvancedWorkoutTemplateSearch({ searchTerm: '', open: false });
          }}
          onAddWorkoutTemplate={(workoutTemplate) => {
            onSelectWorkoutTemplate({
              ...workoutTemplate,
              tags: workoutTemplate.tags.map((t) => t.tag),
            });
            setAdvancedWorkoutTemplateSearch({ searchTerm: '', open: false });
          }}
        />
      )}
    </span>
  );
};

const NoWorkoutTemplateBadge = ({
  setModalOrDrawerOpen,
  setAdvancedWorkoutTemplateSearch,
}: {
  setModalOrDrawerOpen: (open: boolean) => void;
  setAdvancedWorkoutTemplateSearch: (search: AdvancedSearchState) => void;
}) => {
  const { t } = useTranslation();

  const handleClick = () => {
    setModalOrDrawerOpen(true);
    setAdvancedWorkoutTemplateSearch({ searchTerm: '', open: true });
  };

  return (
    <Button
      onClick={handleClick}
      aria-describedby={'workout-template-popover'}
      className="rounded-full"
      variant="outlined"
      sx={{
        display: 'flex',
        borderRadius: '9999px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        fontSize: '12px',
        fontWeight: 500,
        paddingX: '12px',
        height: '18px',
      }}
      size="small"
    >
      {t('addTemplate')}
    </Button>
  );
};
